/**
 * MarketMapRenderer Component
 *
 * Tool renderer for the get_market_map chat agent tool.
 * Displays a treemap-style heatmap visualization of market data.
 */

import { FC, useMemo, useCallback } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import { colors, spacing } from '../../../styles';
import { CustomizedContent, CustomTooltip, CustomContentProps } from '../../MarketMap/shared';
import { ToolRendererProps, MarketMapStock, MarketMapSector, MarketMapOutput } from './types';

// Loading state
const LoadingState: FC<{ message: string }> = ({ message }) => (
  <div
    css={css`
      font-size: 14px;
      color: ${colors.silver};

      html[data-theme='dark'] & {
        color: ${colors.tungsten};
      }
    `}
  >
    {message}
  </div>
);

export const MarketMapRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  const history = useHistory();

  // Parse output to get market map data
  const marketMapData = useMemo((): MarketMapOutput | null => {
    if (typeof output === 'string') {
      try {
        return JSON.parse(output) as MarketMapOutput;
      } catch {
        return null;
      }
    }
    return output as MarketMapOutput;
  }, [output]);

  // Extract stocks from the data structure
  const { stocks, period, viewType } = useMemo(() => {
    if (!marketMapData) {
      return { stocks: [], period: '', viewType: '' };
    }

    // Try to get data from nested structure first, then fallback to root level
    const data = marketMapData.data || marketMapData;
    const periodValue = data.period || '';
    // Support both view and map_type fields
    const viewValue = (data as any).map_type || data.view || '';

    // Handle sector_breakdown type - use sectors array only when sectors are objects (not strings)
    if (data.sectors && data.sectors.length > 0 && typeof data.sectors[0] !== 'string') {
      // Convert sectors to stock-like format for the treemap
      const sectorStocks: MarketMapStock[] = (data.sectors as MarketMapSector[]).map((sector: MarketMapSector) => ({
        symbol: sector.name,
        name: sector.name,
        display_name: sector.name,
        change_percent: sector.change_percent,
        market_cap: sector.market_cap,
      }));
      return { stocks: sectorStocks, period: periodValue, viewType: 'Sector Breakdown' };
    }

    // Handle stocks array (top_movers, global_markets, major_indexes)
    if (data.stocks && data.stocks.length > 0) {
      let viewLabel = 'Top Movers';
      if (viewValue === 'global' || viewValue === 'global_markets') viewLabel = 'Global Markets';
      else if (viewValue === 'indexes' || viewValue === 'major_indexes') viewLabel = 'Major Indexes';
      else if (viewValue === 'sector_breakdown') viewLabel = 'Sector Breakdown';
      return { stocks: data.stocks, period: periodValue, viewType: viewLabel };
    }

    return { stocks: [], period: periodValue, viewType: '' };
  }, [marketMapData]);

  // Transform stocks to treemap format
  const treemapData = useMemo(() => {
    return stocks
      .filter((stock: MarketMapStock) => stock.change_percent != null)
      .map((stock: MarketMapStock) => ({
        name: stock.name,
        symbol: stock.symbol,
        display_name: stock.display_name,
        size: Math.abs(stock.change_percent) + 1, // +1 to ensure minimum size
        change_percent: stock.change_percent,
        market_cap: stock.market_cap,
      }));
  }, [stocks]);

  // Handle cell click - navigate to symbol page
  const handleCellClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (nodeData: any) => {
      // Don't navigate for sector names (they're not symbols)
      if (nodeData?.symbol && viewType !== 'Sector Breakdown') {
        history.push(`/symbol/${nodeData.symbol}`);
      }
    },
    [history, viewType],
  );

  if (isLoading) {
    return <LoadingState message="Loading market map..." />;
  }

  if (!marketMapData || treemapData.length === 0) {
    return <LoadingState message="No market data available" />;
  }

  // Format period for display
  const periodLabel = period ? period.toUpperCase() : '';

  return (
    <div
      css={css`
        font-size: 14px;
        color: ${colors.liquorice};
        line-height: 1.5;

        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      {/* Header with view type and period */}
      {(viewType || periodLabel) && (
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: ${spacing.centi};
            font-size: 12px;
            color: ${colors.silver};

            html[data-theme='dark'] & {
              color: ${colors.tungsten};
            }
          `}
        >
          <span>{viewType}</span>
          {periodLabel && <span>{periodLabel}</span>}
        </div>
      )}

      {/* Treemap */}
      <div
        css={css`
          width: 100%;
          height: 250px;
          border-radius: 8px;
          overflow: hidden;
        `}
      >
        <ResponsiveContainer width="100%" height={250}>
          <Treemap
            data={treemapData}
            dataKey="size"
            aspectRatio={4 / 3}
            stroke="#fff"
            content={(props: CustomContentProps) => <CustomizedContent {...props} />}
            onClick={handleCellClick}
            isAnimationActive={false}
          >
            <Tooltip content={<CustomTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
